import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  AlertCircle,
  Calendar,
  ChevronLeft,
  Clock,
  Droplet,
  Dumbbell,
  FileText,
  Heart,
  Pill,
  RefreshCw,
  Ruler,
  Scale,
  ShieldAlert,
  Sparkles,
  Syringe,
  User,
  Users,
  Wine,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { consultationApi } from '@/services/clinicApi';
import api, { getLocalizedValue, medicalService } from '@/services/api';

const readMap = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const readList = (value) => (Array.isArray(value) ? value : []);

const readString = (value) => {
  if (value == null || value === false) return '';
  if (typeof value === 'object' && !Array.isArray(value)) {
    for (const key of ['name', 'display_name', 'value', 'ar', 'en', 'note', 'notes']) {
      const resolved = readString(value[key]);
      if (resolved) return resolved;
    }
    return '';
  }

  const normalized = String(value).trim();
  return normalized || '';
};

const readBool = (value, fallback = false) => {
  if (value == null) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;

  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n', ''].includes(normalized)) return false;
  return fallback;
};

const readNumber = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const stripHtml = (value) => readString(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const extractNamedValues = (value, lang) =>
  readList(value)
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (!item || typeof item !== 'object') return '';
      return (
        getLocalizedValue(item.name, lang) ||
        getLocalizedValue(item.label, lang) ||
        readString(item.name) ||
        readString(item.label) ||
        readString(item.display_name)
      );
    })
    .filter(Boolean);

const formatLifestyleValue = (value, lang) => {
  const normalized = readString(value).toLowerCase();
  if (!normalized || normalized === 'none') {
    return lang === 'ar' ? 'لا يوجد' : 'None';
  }
  if (normalized === 'daily') {
    return lang === 'ar' ? 'يومي' : 'Daily';
  }
  if (normalized === 'weekly') {
    return lang === 'ar' ? 'أسبوعي' : 'Weekly';
  }
  if (normalized === 'occasional') {
    return lang === 'ar' ? 'أحيانًا' : 'Occasional';
  }
  if (normalized === 'regular') {
    return lang === 'ar' ? 'منتظم' : 'Regular';
  }
  return readString(value);
};

const getPatientName = (patientData, lang) => {
  const localizedName = getLocalizedValue(patientData.name, lang) || readString(patientData.name);
  if (localizedName) return localizedName;

  const firstName =
    getLocalizedValue(patientData.first_name, lang) ||
    getLocalizedValue(patientData.firstname, lang) ||
    readString(patientData.first_name) ||
    readString(patientData.firstname);
  const lastName =
    getLocalizedValue(patientData.last_name, lang) ||
    getLocalizedValue(patientData.lastname, lang) ||
    readString(patientData.last_name) ||
    readString(patientData.lastname);

  return `${firstName} ${lastName}`.trim() || (lang === 'ar' ? 'مريض' : 'Patient');
};

const normalizeGender = (value, lang) => {
  const normalized = readString(value).toLowerCase();
  if (normalized === 'male') return lang === 'ar' ? 'ذكر' : 'Male';
  if (normalized === 'female') return lang === 'ar' ? 'أنثى' : 'Female';
  return readString(value);
};

const calculateAge = (dateValue) => {
  if (!dateValue) return null;
  const birthDate = new Date(dateValue);
  if (Number.isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
};

const parseConsultationDate = (item) => {
  const candidate =
    item.date ||
    item.consultation_date ||
    item.appointment_date ||
    item.appointment_at ||
    item.created_at;
  if (!candidate) return null;
  const parsed = new Date(candidate);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const buildConsultations = (items, lang) =>
  readList(items)
    .map((item, index) => {
      const row = readMap(item);
      return {
        id: row.id || row.consultation_id || index + 1,
        appointmentId: row.appointment_id || 0,
        date: parseConsultationDate(row),
        note: stripHtml(row.note || row.notes),
        diagnosis: stripHtml(row.diagnosis),
        chiefComplaint: stripHtml(row.chief_complaint),
        medications: extractNamedValues(
          row.permanent_medications || row.permanent_medication_ids || row.medications,
          lang
        ),
      };
    })
    .filter((item) => item.id || item.appointmentId)
    .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));

const getPrimaryNotes = (consultations, fallbacks) => {
  for (const item of consultations) {
    if (item.note) return item.note;
    if (item.diagnosis) return item.diagnosis;
    if (item.chiefComplaint) return item.chiefComplaint;
  }

  for (const candidate of fallbacks) {
    if (candidate) return candidate;
  }

  return '';
};

const extractResponseObject = (response) => {
  const directData = readMap(response?.data?.data);
  if (Object.keys(directData).length > 0) return directData;
  return readMap(response?.data);
};

const DetailListSection = ({ icon: Icon, title, values, emptyLabel, colorClass = 'text-slate-500' }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2">
      <Icon className={`h-4 w-4 ${colorClass}`} />
      <h3 className="text-base font-semibold text-slate-900 dark:text-white">{title}</h3>
    </div>
    {values.length === 0 ? (
      <p className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-500 dark:bg-slate-700/40 dark:text-slate-400">
        {emptyLabel}
      </p>
    ) : (
      <div className="flex flex-wrap gap-2">
        {values.map((value, index) => (
          <Badge
            key={`${title}-${value}-${index}`}
            variant="secondary"
            className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200"
          >
            {value}
          </Badge>
        ))}
      </div>
    )}
  </div>
);

const PatientDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPatientDetail = useCallback(async () => {
    setLoading(true);
    try {
      const [patientRes, historyRes, consultationsRes] = await Promise.allSettled([
        api.get(`/patients/${id}`, {
          params: {
            with: 'user;medicalHistory;appointments.appointmentStatus',
          },
        }),
        medicalService.getPatientMedicalRecord(id),
        consultationApi.getPatientConsultations(id),
      ]);

      const patientPayload =
        patientRes.status === 'fulfilled' ? extractResponseObject(patientRes.value) : {};

      const historyPayload =
        historyRes.status === 'fulfilled' ? extractResponseObject(historyRes.value) : {};

      const patientFromHistory = readMap(historyPayload.patient);
      const medicalHistory =
        readMap(historyPayload.medical_history).patient_id || Object.keys(readMap(historyPayload.medical_history)).length
          ? readMap(historyPayload.medical_history)
          : readMap(patientPayload.medicalHistory);

      const sourcePatient =
        Object.keys(patientFromHistory).length > 0 ? { ...patientPayload, ...patientFromHistory } : patientPayload;

      const appointments = readList(sourcePatient.appointments);
      const consultationsSource =
        consultationsRes.status === 'fulfilled'
          ? readList(consultationsRes.value.data?.data).length > 0
            ? readList(consultationsRes.value.data?.data)
            : readList(consultationsRes.value.data)
          : [];
      const consultations = buildConsultations(consultationsSource, lang);

      const dateOfBirth = sourcePatient.date_of_birth || sourcePatient.birthday || '';
      const age = calculateAge(dateOfBirth);
      const heightValue = readNumber(sourcePatient.height ?? medicalHistory.height);
      const weightValue = readNumber(sourcePatient.weight ?? medicalHistory.weight);
      const smokingYears = readNumber(medicalHistory.smoking_years) || 0;
      const isSmoker = readBool(medicalHistory.is_smoker, smokingYears > 0);
      const chronicDiseases = extractNamedValues(
        medicalHistory.chronic_diseases || medicalHistory.chronic_disease,
        lang
      );
      const permanentMedications = extractNamedValues(
        medicalHistory.permanent_medications || medicalHistory.permanent_medication,
        lang
      );
      const allergies = extractNamedValues(medicalHistory.allergies, lang);
      const surgeries = extractNamedValues(medicalHistory.surgeries, lang);
      const vaccines = extractNamedValues(medicalHistory.vaccines, lang);

      const transformedPatient = {
        id: sourcePatient.id || Number(id),
        name: getPatientName(sourcePatient, lang),
        image:
          sourcePatient.image ||
          readMap(readList(sourcePatient.media)[0]).url ||
          sourcePatient.user?.avatar ||
          null,
        gender: normalizeGender(sourcePatient.gender, lang),
        age,
        phone: readString(sourcePatient.mobile || sourcePatient.phone || sourcePatient.phone_number),
        email: readString(readMap(sourcePatient.user).email || sourcePatient.email),
        bloodType: readString(sourcePatient.blood_type || medicalHistory.blood_type),
        height: heightValue,
        weight: weightValue,
        dateOfBirth,
        appointmentsCount: appointments.length,
        consultationsCount: consultations.length,
        notes: getPrimaryNotes(consultations, [
          stripHtml(sourcePatient.notes),
          stripHtml(medicalHistory.notes),
          stripHtml(medicalHistory.medical_notes),
        ]),
        familyHistory: stripHtml(medicalHistory.family_history),
        smokingText: isSmoker
          ? smokingYears > 0
            ? `${smokingYears} ${lang === 'ar' ? 'سنة' : 'years'}`
            : lang === 'ar'
            ? 'نعم'
            : 'Yes'
          : lang === 'ar'
          ? 'لا'
          : 'No',
        alcoholText: formatLifestyleValue(medicalHistory.alcohol_consumption, lang),
        exerciseText: formatLifestyleValue(medicalHistory.exercise_frequency, lang),
        chronicDiseases,
        permanentMedications,
        allergies,
        surgeries,
        vaccines,
        consultations,
      };

      setPatient(transformedPatient);
    } catch (error) {
      console.error('Failed to fetch patient:', error);
      toast.error(lang === 'ar' ? 'فشل في تحميل بيانات المريض' : 'Failed to load patient data');
      setPatient(null);
    } finally {
      setLoading(false);
    }
  }, [id, lang]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    void fetchPatientDetail();
  }, [fetchPatientDetail, isAuthenticated, navigate, location.state?.refreshAt]);

  if (!isAuthenticated) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
        <div className="mx-auto max-w-5xl space-y-6">
          <Skeleton className="h-52 rounded-3xl" />
          <Skeleton className="h-20 rounded-2xl" />
          <Skeleton className="h-72 rounded-3xl" />
          <Skeleton className="h-72 rounded-3xl" />
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center">
          <User className="mx-auto mb-4 h-16 w-16 text-slate-300 dark:text-slate-600" />
          <p className="text-slate-500 dark:text-slate-400">
            {lang === 'ar' ? 'المريض غير موجود' : 'Patient not found'}
          </p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/doctor/patients')}>
            {lang === 'ar' ? 'العودة للقائمة' : 'Back to List'}
          </Button>
        </div>
      </div>
    );
  }

  const summaryCards = [
    {
      key: 'appointments',
      icon: Calendar,
      label: lang === 'ar' ? 'المواعيد' : 'Appointments',
      value: patient.appointmentsCount,
    },
    {
      key: 'consultations',
      icon: FileText,
      label: lang === 'ar' ? 'المعاينات' : 'Consultations',
      value: patient.consultationsCount,
    },
    {
      key: 'weight',
      icon: Scale,
      label: lang === 'ar' ? 'الوزن' : 'Weight',
      value: patient.weight ? `${patient.weight} KG` : lang === 'ar' ? 'غير مسجل' : 'Not set',
    },
    {
      key: 'height',
      icon: Ruler,
      label: lang === 'ar' ? 'الطول' : 'Height',
      value: patient.height ? `${patient.height} CM` : lang === 'ar' ? 'غير مسجل' : 'Not set',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="patient-detail">
      <div className="border-b bg-white dark:border-slate-700 dark:bg-slate-800">
        <div className="mx-auto max-w-5xl px-4 py-6">
          <div className="mb-6 flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/patients')}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button variant="outline" size="icon" onClick={fetchPatientDetail}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <div className="rounded-[28px] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 p-6 text-white shadow-xl shadow-slate-900/20">
            <div className="flex flex-col gap-6 md:flex-row md:items-center">
              <Avatar className="h-24 w-24 border-4 border-white/20">
                <AvatarImage src={patient.image} />
                <AvatarFallback className="bg-white/90 text-3xl font-bold text-slate-800">
                  {patient.name.charAt(0)}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 space-y-3">
                <h1 className="text-3xl font-bold">{patient.name}</h1>
                <div className="flex flex-wrap items-center gap-3 text-sm text-slate-200">
                  {patient.age != null && (
                    <span>{patient.age} {lang === 'ar' ? 'سنة' : 'years'}</span>
                  )}
                  {patient.gender && (
                    <>
                      <span className="hidden md:inline">•</span>
                      <span>{patient.gender}</span>
                    </>
                  )}
                  {patient.bloodType && (
                    <>
                      <span className="hidden md:inline">•</span>
                      <Badge className="border-white/20 bg-white/10 text-white hover:bg-white/10">
                        <Droplet className="mr-1 h-3 w-3" />
                        {patient.bloodType}
                      </Badge>
                    </>
                  )}
                </div>
                <p className="max-w-2xl text-base text-slate-200">
                  {lang === 'ar'
                    ? `لدى هذا المريض ${patient.appointmentsCount} موعد و ${patient.consultationsCount} معاينة مسجلة.`
                    : `This patient has ${patient.appointmentsCount} appointments and ${patient.consultationsCount} recorded consultations.`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-6">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-6 h-auto w-full justify-start gap-2 rounded-2xl bg-slate-200/70 p-2 dark:bg-slate-800">
            <TabsTrigger value="overview">{lang === 'ar' ? 'نظرة عامة' : 'Overview'}</TabsTrigger>
            <TabsTrigger value="history">{lang === 'ar' ? 'السجل الطبي' : 'Medical History'}</TabsTrigger>
            <TabsTrigger value="consultations">{lang === 'ar' ? 'المعاينات' : 'Consultations'}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {summaryCards.map(({ key, icon: Icon, label, value }) => (
                <Card key={key} className="rounded-3xl border-0 shadow-sm dark:bg-slate-800">
                  <CardContent className="flex items-center gap-4 p-5">
                    <div className="rounded-2xl bg-slate-100 p-3 dark:bg-slate-700">
                      <Icon className="h-5 w-5 text-slate-700 dark:text-slate-200" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
                      <p className="text-lg font-bold text-slate-900 dark:text-white">{value}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="rounded-3xl border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-white">
                  <Sparkles className="h-5 w-5 text-indigo-500" />
                  {lang === 'ar' ? 'معلومات سريعة' : 'Quick Details'}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-700/40">
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'العمر' : 'Age'}</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
                    {patient.age != null ? `${patient.age}` : lang === 'ar' ? 'غير مسجل' : 'Not set'}
                  </p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-700/40">
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'الجنس' : 'Gender'}</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
                    {patient.gender || (lang === 'ar' ? 'غير مسجل' : 'Not set')}
                  </p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-700/40">
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'زمرة الدم' : 'Blood Type'}</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
                    {patient.bloodType || (lang === 'ar' ? 'غير مسجل' : 'Not set')}
                  </p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-700/40">
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'تاريخ الميلاد' : 'Birth Date'}</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
                    {patient.dateOfBirth
                      ? format(new Date(patient.dateOfBirth), 'PP', { locale })
                      : lang === 'ar'
                      ? 'غير مسجل'
                      : 'Not set'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-3xl border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-white">
                  <FileText className="h-5 w-5 text-indigo-500" />
                  {lang === 'ar' ? 'الملاحظات' : 'Notes'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="rounded-2xl bg-slate-50 px-4 py-5 text-base leading-7 text-slate-700 dark:bg-slate-700/40 dark:text-slate-200">
                  {patient.notes || (lang === 'ar' ? 'لا توجد ملاحظات مضافة بعد' : 'No notes added yet')}
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            <Card className="rounded-3xl border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-white">
                  <Heart className="h-5 w-5 text-rose-500" />
                  {lang === 'ar' ? 'التاريخ المرضي' : 'Medical History'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-wrap gap-2">
                  <Badge className="rounded-full bg-slate-100 px-3 py-1 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                    <Droplet className="mr-1 h-3 w-3" />
                    {lang === 'ar' ? 'زمرة الدم' : 'Blood Type'}: {patient.bloodType || (lang === 'ar' ? 'غير مسجل' : 'Not set')}
                  </Badge>
                  <Badge className="rounded-full bg-slate-100 px-3 py-1 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                    <ShieldAlert className="mr-1 h-3 w-3" />
                    {lang === 'ar' ? 'التدخين' : 'Smoking'}: {patient.smokingText}
                  </Badge>
                  <Badge className="rounded-full bg-slate-100 px-3 py-1 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                    <Wine className="mr-1 h-3 w-3" />
                    {lang === 'ar' ? 'الكحول' : 'Alcohol'}: {patient.alcoholText}
                  </Badge>
                  <Badge className="rounded-full bg-slate-100 px-3 py-1 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                    <Dumbbell className="mr-1 h-3 w-3" />
                    {lang === 'ar' ? 'الرياضة' : 'Exercise'}: {patient.exerciseText}
                  </Badge>
                </div>

                {patient.familyHistory && (
                  <div className="space-y-2">
                    <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                      {lang === 'ar' ? 'تاريخ عائلي' : 'Family History'}
                    </h3>
                    <p className="rounded-2xl bg-slate-50 px-4 py-4 text-slate-700 dark:bg-slate-700/40 dark:text-slate-200">
                      {patient.familyHistory}
                    </p>
                  </div>
                )}

                <div className="grid gap-6 lg:grid-cols-2">
                  <DetailListSection
                    icon={Heart}
                    title={lang === 'ar' ? 'الأمراض المزمنة' : 'Chronic Diseases'}
                    values={patient.chronicDiseases}
                    emptyLabel={lang === 'ar' ? 'لا توجد أمراض مزمنة مسجلة' : 'No chronic diseases recorded'}
                    colorClass="text-rose-500"
                  />
                  <DetailListSection
                    icon={Pill}
                    title={lang === 'ar' ? 'الأدوية الدائمة' : 'Permanent Medications'}
                    values={patient.permanentMedications}
                    emptyLabel={lang === 'ar' ? 'لا توجد أدوية دائمة مسجلة' : 'No permanent medications recorded'}
                    colorClass="text-blue-500"
                  />
                  <DetailListSection
                    icon={AlertCircle}
                    title={lang === 'ar' ? 'الحساسيات' : 'Allergies'}
                    values={patient.allergies}
                    emptyLabel={lang === 'ar' ? 'لا توجد حساسيات مسجلة' : 'No allergies recorded'}
                    colorClass="text-amber-500"
                  />
                  <DetailListSection
                    icon={Users}
                    title={lang === 'ar' ? 'العمليات الجراحية' : 'Surgeries'}
                    values={patient.surgeries}
                    emptyLabel={lang === 'ar' ? 'لا توجد عمليات جراحية مسجلة' : 'No surgeries recorded'}
                    colorClass="text-emerald-500"
                  />
                  <div className="lg:col-span-2">
                    <DetailListSection
                      icon={Syringe}
                      title={lang === 'ar' ? 'اللقاحات' : 'Vaccines'}
                      values={patient.vaccines}
                      emptyLabel={lang === 'ar' ? 'لا توجد لقاحات مسجلة' : 'No vaccines recorded'}
                      colorClass="text-violet-500"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="consultations" className="space-y-6">
            <Card className="rounded-3xl border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-white">
                  <FileText className="h-5 w-5 text-indigo-500" />
                  {lang === 'ar' ? 'المعاينات السابقة' : 'Previous Consultations'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {patient.consultations.length === 0 ? (
                  <p className="rounded-2xl bg-slate-50 px-4 py-6 text-center text-slate-500 dark:bg-slate-700/40 dark:text-slate-400">
                    {lang === 'ar' ? 'لا توجد معاينات مسجلة بعد' : 'No consultations yet'}
                  </p>
                ) : (
                  <div className="space-y-4">
                    {patient.consultations.map((consultation, index) => (
                      <div
                        key={`${consultation.id}-${consultation.appointmentId || index}`}
                        className="rounded-3xl bg-slate-50 p-5 shadow-sm dark:bg-slate-700/40"
                      >
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div className="space-y-1">
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {lang === 'ar' ? 'رقم المعاينة' : 'Consultation Number'}
                            </p>
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">
                              {consultation.id || consultation.appointmentId || index + 1}
                            </p>
                          </div>
                          <div className="space-y-1 md:text-right">
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {lang === 'ar' ? 'تاريخ المعاينة' : 'Consultation Date'}
                            </p>
                            <p className="flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white md:justify-end">
                              <Clock className="h-4 w-4 text-slate-400" />
                              {consultation.date
                                ? format(consultation.date, 'PP', { locale })
                                : lang === 'ar'
                                ? 'غير محدد'
                                : 'Unknown'}
                            </p>
                          </div>
                        </div>

                        {(consultation.note || consultation.diagnosis || consultation.chiefComplaint) && (
                          <div className="mt-4 space-y-3">
                            {consultation.note && (
                              <div>
                                <p className="mb-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                                  {lang === 'ar' ? 'الملاحظات' : 'Notes'}
                                </p>
                                <p className="text-slate-700 dark:text-slate-200">{consultation.note}</p>
                              </div>
                            )}
                            {consultation.diagnosis && (
                              <div>
                                <p className="mb-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                                  {lang === 'ar' ? 'التشخيص' : 'Diagnosis'}
                                </p>
                                <p className="text-slate-700 dark:text-slate-200">{consultation.diagnosis}</p>
                              </div>
                            )}
                            {consultation.chiefComplaint && (
                              <div>
                                <p className="mb-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                                  {lang === 'ar' ? 'الشكوى الرئيسية' : 'Chief Complaint'}
                                </p>
                                <p className="text-slate-700 dark:text-slate-200">{consultation.chiefComplaint}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {consultation.medications.length > 0 && (
                          <div className="mt-4">
                            <p className="mb-2 text-sm font-medium text-slate-500 dark:text-slate-400">
                              {lang === 'ar' ? 'الأدوية' : 'Medications'}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {consultation.medications.map((medication, medIndex) => (
                                <Badge
                                  key={`${consultation.id}-med-${medication}-${medIndex}`}
                                  className="rounded-full bg-indigo-100 px-3 py-1 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-200"
                                >
                                  {medication}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {consultation.id > 0 && (
                          <div className="mt-5">
                            <Button
                              onClick={() =>
                                navigate(`/doctor/consultations/${consultation.id}/edit`, {
                                  state: { patientId: patient.id },
                                })
                              }
                              className="w-full rounded-2xl"
                            >
                              {lang === 'ar' ? 'فتح وتعديل المعاينة' : 'Open and edit consultation'}
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PatientDetailPage;
